import { LIMITS } from "@listou/shared/constants";
import type { PublicWish } from "@listou/shared/types/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ListPage, type MyReservation } from "../components/list-page";
import { LogoMark } from "../components/logo";
import { OccasionLinks } from "../components/site-header";
import { useToast } from "../components/toast";
import { Dialog as Modal } from "../components/ui/dialog";
import { NumberField } from "../components/ui/number-field";
import { PageLoader } from "../components/ui/spinner";
import { ApiError, publicApi } from "../lib/api";
import { rememberSharedList } from "../lib/attribution";
import { formatPrice } from "../lib/format";
import { seo } from "../lib/head";
import { listsQuery, publicListQuery } from "../lib/queries";
import { forgetReservation, rememberReservation, reservationsFor } from "../lib/reservations-store";
import { errorMessage } from "../lib/session";

export const Route = createFileRoute("/l/$slug")({
  // The confirmation email links here with ?cancel=<reservationId>.<token>.
  validateSearch: (search: Record<string, unknown>): { cancel?: string } => ({
    cancel: typeof search.cancel === "string" ? search.cancel : undefined,
  }),
  staticData: { ssr: true },
  // query() rather than prefetchQuery(): head() needs the list.
  loader: async ({ context: { queryClient }, params: { slug } }) => {
    try {
      return await queryClient.query(publicListQuery(slug));
    } catch (error) {
      // A 404 status for a link that leads nowhere, with this page's own explanation.
      if (error instanceof ApiError && error.status === 404) throw notFound();
      // Anything else stays in the query cache, and the page shows its own message.
      return null;
    }
  },
  notFoundComponent: () => <ListUnavailable reason="missing" />,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { list, wishes } = loaderData;
    return seo({
      title: `${list.headline} · Listou`,
      description:
        list.description ??
        `${wishes.length} envie${wishes.length > 1 ? "s" : ""} à offrir. Réservez un cadeau en un clic, sans compte.`,
      image: `${list.shareImageUrl}?format=og`,
      // Shared by link: search engines have no business listing someone's baby registry.
      noindex: true,
    });
  },
  component: PublicListPage,
});

function PublicListPage() {
  const { slug } = Route.useParams();
  const { cancel } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const toast = useToast();
  // The loader already asked. Refetching on mount would turn an error it got (the owner's
  // own list) back into a spinner, on the server and again while hydrating.
  const list = useQuery({ ...publicListQuery(slug), retryOnMount: false });
  // Read after mounting: the reservations live in this browser's storage, which the
  // server rendering the page cannot see. Reading them during the first render would make
  // it differ from the server's HTML.
  const [mine, setMine] = useState<MyReservation[]>([]);
  const [reserving, setReserving] = useState<PublicWish | null>(null);

  useEffect(() => setMine(reservationsFor(slug)), [slug]);
  // A guest who signs up later counts as brought by this list (the k-factor in /admin).
  useEffect(() => rememberSharedList(slug), [slug]);

  const refresh = () => {
    setMine(reservationsFor(slug));
    void queryClient.invalidateQueries({ queryKey: publicListQuery(slug).queryKey });
  };

  const cancelReservation = useMutation({
    mutationFn: ({ id, token }: { id: string; token: string }) =>
      publicApi.cancelReservation(id, token),
    onSuccess: (_data, { id }) => {
      forgetReservation(id);
      refresh();
      toast("Réservation annulée. Le cadeau est de nouveau disponible.");
      void navigate({ search: {} });
    },
    onError: (error) => {
      // Already cancelled elsewhere: nothing left to undo, so forget it here too.
      if (error instanceof ApiError && error.status === 404) {
        void navigate({ search: {} });
        toast("Cette réservation n'existe plus.");
        return;
      }
      toast(errorMessage(error, "La réservation n'a pas pu être annulée."), "error");
    },
  });

  if (list.isPending) return <PageLoader />;

  if (list.isError) {
    const reason =
      list.error instanceof ApiError && list.error.code === "own_list"
        ? "own_list"
        : list.error instanceof ApiError && list.error.status === 404
          ? "missing"
          : "failed";
    return <ListUnavailable reason={reason} onRetry={() => void list.refetch()} />;
  }

  const { list: data, wishes } = list.data;
  const [cancelId, cancelToken] = cancel?.split(".") ?? [];
  const cancelWish = cancelId
    ? wishes.find((wish) => mine.some((item) => item.id === cancelId && item.wishId === wish.id))
    : undefined;

  const share = async () => {
    const url = window.location.origin + `/l/${slug}`;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title: data.headline, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast("Lien copié");
    } catch {
      // The person closed the share sheet.
    }
  };

  return (
    <>
      {/* The brand, plainly: a guest landing from WhatsApp should know where they are. */}
      <div className="border-b-2 border-line bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <LogoMark className="size-8 shrink-0" />
            <span className="text-muted">
              Liste réalisée avec <strong className="font-display text-ink">Listou</strong>
            </span>
          </Link>
          <div className="flex shrink-0 items-center gap-1">
            {/* Guests are the ones with questions: how to reserve, deliver, cancel. */}
            <Link to="/aide" hash="offrir" className="btn-ghost btn-sm hidden sm:inline-flex">
              Comment ça marche ?
            </Link>
            <Link to="/" className="btn-secondary btn-sm">
              Créer ma liste
            </Link>
          </div>
        </div>
      </div>
      <ListPage
        list={data}
        wishes={wishes}
        mine={mine}
        onReserve={setReserving}
        onCancel={(reservation) => {
          const token = reservationsFor(slug).find((item) => item.id === reservation.id)?.token;
          if (token) cancelReservation.mutate({ id: reservation.id, token });
        }}
        actions={
          <button type="button" className="btn-secondary" onClick={() => void share()}>
            Partager cette liste
          </button>
        }
      />

      <footer className="border-t-2 border-line bg-surface">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-4 px-4 py-8 sm:px-6">
          <p className="flex items-center gap-2 text-muted">
            <LogoMark className="size-6" /> Une liste faite avec Listou
          </p>
          <div className="flex flex-wrap gap-2">
            <Link to="/aide" hash="offrir" className="btn-ghost">
              Aide
            </Link>
            <Link to="/" className="btn-secondary">
              Créer ma liste à moi
            </Link>
          </div>
          <OccasionLinks className="w-full text-sm text-muted" />{" "}
        </div>
      </footer>

      <ReserveDialog
        slug={slug}
        wish={reserving}
        onClose={() => setReserving(null)}
        onStale={refresh}
        onReserved={(reservation) => {
          rememberReservation(reservation.id, {
            token: reservation.cancelToken,
            wishId: reservation.wishId,
            slug,
          });
          refresh();
        }}
      />

      <Modal
        open={Boolean(cancelId && cancelToken)}
        onClose={() => void navigate({ search: {} })}
        title="Annuler votre réservation ?"
      >
        <p className="text-lg">
          {cancelWish
            ? `Le cadeau « ${cancelWish.title} » redeviendra disponible pour les autres.`
            : "Le cadeau redeviendra disponible pour les autres."}
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-danger"
            disabled={cancelReservation.isPending}
            onClick={() => {
              if (cancelId && cancelToken)
                cancelReservation.mutate({ id: cancelId, token: cancelToken });
            }}
          >
            {cancelReservation.isPending ? "Annulation…" : "Oui, annuler"}
          </button>
          <button type="button" className="btn-ghost" onClick={() => void navigate({ search: {} })}>
            Garder ma réservation
          </button>
        </div>
      </Modal>
    </>
  );
}

/** Why the list is not shown: a dead link, the owner's own list (anti-spoil), or a failure. */
function ListUnavailable({
  reason,
  onRetry,
}: {
  reason: "missing" | "own_list" | "failed";
  onRetry?: () => void;
}) {
  if (reason === "own_list") return <OwnList />;
  return (
    <main className="mx-auto grid max-w-md justify-items-start gap-4 px-4 py-24">
      <LogoMark className="size-12" />
      <h1 className="text-4xl font-semibold">
        {reason === "missing" ? "Cette liste n'est pas disponible" : "La liste n'a pas pu s'ouvrir"}
      </h1>
      <p className="text-lg text-muted">
        {reason === "missing"
          ? "Le lien a peut-être changé, ou la liste est privée pour le moment. Demandez le nouveau lien à la personne qui vous l'a envoyé."
          : "Vérifiez votre connexion et réessayez."}
      </p>
      {reason === "failed" && onRetry && (
        <button type="button" className="btn" onClick={onRetry}>
          Réessayer
        </button>
      )}
    </main>
  );
}

/**
 * The owner opened their own link. The guests' page would tell them what is reserved, so
 * they get the preview instead: the same page, with every reservation left out. The error
 * does not carry the list id; the owner is signed in, so their own lists find it by slug.
 */
function OwnList() {
  const { slug } = Route.useParams();
  const lists = useQuery(listsQuery);
  const mine = lists.data?.lists.find((list) => list.slug === slug);

  return (
    <main className="mx-auto grid max-w-lg justify-items-start gap-5 px-4 py-24">
      <LogoMark className="size-12" />
      <h1 className="text-4xl font-semibold">Chut, c'est votre liste !</h1>
      <p className="text-lg">
        Vos proches voient ici ce qui est déjà réservé. Pas vous : on vous le cache exprès, pour que
        la surprise tienne jusqu'au bout.
      </p>
      <p className="text-muted">
        L'aperçu vous montre la page exactement comme eux, sauf les réservations : chaque cadeau y
        paraît encore libre. C'est normal, et c'est voulu.
      </p>
      <div className="flex flex-wrap gap-2">
        {mine ? (
          <>
            <Link to="/mes-listes/$listId/preview" params={{ listId: mine.id }} className="btn">
              Voir la page comme mes proches
            </Link>
            <Link to="/mes-listes/$listId" params={{ listId: mine.id }} className="btn-secondary">
              Modifier ma liste
            </Link>
          </>
        ) : (
          <Link to="/mes-listes" className="btn">
            Ouvrir mes listes
          </Link>
        )}
      </div>
    </main>
  );
}

function ReserveDialog({
  slug,
  wish,
  onClose,
  onStale,
  onReserved,
}: {
  slug: string;
  wish: PublicWish | null;
  onClose: () => void;
  onStale: () => void;
  onReserved: (reservation: { id: string; wishId: string; cancelToken: string }) => void;
}) {
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [quantity, setQuantity] = useState(1);
  // The receipt's address: known only now that the guest has committed to a gift.
  const [done, setDone] = useState<{ deliveryAddress: string | null } | null>(null);

  const remaining = wish ? Math.max(1, wish.quantity - wish.reservedQuantity) : 1;

  const reserve = useMutation({
    mutationFn: () => {
      if (!wish) throw new Error("No wish selected");
      return publicApi.reserve(slug, {
        wishId: wish.id,
        quantity,
        guestName: guestName.trim() || null,
        guestEmail: guestEmail.trim() || null,
        anonymous,
      });
    },
    onSuccess: ({ reservation }) => {
      onReserved(reservation);
      setDone({ deliveryAddress: reservation.deliveryAddress });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "not_available") onStale();
    },
  });

  useEffect(() => {
    // A fresh form for every wish, but the name is kept: the same person often gives two.
    setQuantity(1);
    setDone(null);
    reserve.reset();
    // `reserve` is a new object each render; only the wish should reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wish?.id]);

  const unavailable = reserve.error instanceof ApiError && reserve.error.code === "not_available";

  return (
    <Modal
      open={wish !== null}
      onClose={onClose}
      title={done ? "C'est noté !" : "Offrir ce cadeau"}
    >
      {wish && done && (
        <div className="grid gap-4">
          <p className="text-lg">
            Vous offrez « {wish.title} ». Les autres invités voient qu'il est pris, et la personne
            qui le reçoit n'en saura rien.
          </p>
          <HowToGive deliveryAddress={done.deliveryAddress} />
          {guestEmail.trim() && (
            <p className="text-muted">
              Un email de confirmation part vers {guestEmail.trim()}, avec un lien pour annuler si
              besoin.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {wish.goUrl && (
              <a
                className="btn"
                href={wish.goUrl}
                target="_blank"
                rel="sponsored nofollow noopener"
              >
                Acheter {wish.merchant ? `sur ${wish.merchant}` : "le cadeau"}
              </a>
            )}
            <button type="button" className="btn-secondary" onClick={onClose}>
              Revenir à la liste
            </button>
          </div>
        </div>
      )}

      {wish && !done && (
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            reserve.mutate();
          }}
        >
          <div className="flex items-center gap-4 rounded-2xl bg-ink/[0.04] p-3">
            {wish.imageUrl && (
              <img
                src={wish.imageUrl}
                alt=""
                className="size-16 rounded-xl bg-white object-contain p-1"
              />
            )}
            <div className="grid">
              <p className="font-display text-lg font-bold">{wish.title}</p>
              {wish.priceCents !== null && (
                <p className="text-muted">Environ {formatPrice(wish.priceCents, wish.currency)}</p>
              )}
            </div>
          </div>

          <label className="label text-base">
            Votre prénom
            <input
              className="input text-lg"
              value={guestName}
              maxLength={LIMITS.guestNameMax}
              placeholder="Mamie, Tonton Clément…"
              autoComplete="given-name"
              onChange={(event) => setGuestName(event.target.value)}
            />
            <span className="font-normal text-muted">
              Seuls les organisateurs le verront, pour vous remercier. Facultatif.
            </span>
          </label>

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 size-5 shrink-0 accent-[var(--color-accent)]"
              checked={anonymous}
              onChange={(event) => setAnonymous(event.target.checked)}
            />
            <span className="grid gap-0.5">
              <span className="font-bold">Rester anonyme</span>
              <span className="text-muted">
                Même après la fête, la personne qui reçoit ne saura pas que c'est vous.
              </span>
            </span>
          </label>

          <label className="label text-base">
            Votre email
            <input
              className="input text-lg"
              type="email"
              value={guestEmail}
              placeholder="Pour recevoir le lien d'annulation"
              autoComplete="email"
              onChange={(event) => setGuestEmail(event.target.value)}
            />
            <span className="font-normal text-muted">Facultatif, et jamais montré à personne.</span>
          </label>

          {remaining > 1 && (
            <NumberField
              label={`Combien en offrez-vous ? (${remaining} encore souhaités)`}
              value={quantity}
              onChange={(value) => setQuantity(Math.min(remaining, Math.max(1, value ?? 1)))}
              min={1}
              max={remaining}
            />
          )}

          {reserve.isError && (
            <p className="text-danger">
              {unavailable
                ? "Quelqu'un vient de réserver ce cadeau. La liste a été mise à jour."
                : errorMessage(reserve.error, "La réservation n'a pas pu être enregistrée.")}
            </p>
          )}

          <button
            type="submit"
            className="btn min-h-13 text-lg"
            disabled={reserve.isPending || unavailable}
          >
            {reserve.isPending ? "Réservation…" : "Je réserve ce cadeau"}
          </button>
          {/* A new tab: the form keeps what was typed. */}
          <a
            href="/aide#offrir"
            target="_blank"
            rel="noopener"
            className="justify-self-center text-sm text-muted underline underline-offset-2 hover:text-ink"
          >
            Comment ça marche ? Paiement, livraison, annulation
          </a>
        </form>
      )}
    </Modal>
  );
}

/**
 * What happens next, since Listou handles neither payment nor delivery: the guest buys
 * wherever they like and hands the gift over their own way.
 */
function HowToGive({ deliveryAddress }: { deliveryAddress: string | null }) {
  return (
    <div className="grid gap-3 rounded-2xl bg-ink/[0.04] p-4">
      <p className="font-bold">Et maintenant ?</p>
      <ul className="grid list-disc gap-1.5 pl-5">
        <li>Achetez-le sur le site indiqué, ou dans la boutique de votre choix.</li>
        <li>
          {deliveryAddress
            ? "Faites-le livrer à l'adresse ci-dessous, ou chez vous pour l'offrir en main propre."
            : "Faites-le livrer chez vous, puis offrez-le en main propre."}
        </li>
        <li>Un empêchement ? Vous pourrez annuler votre réservation à tout moment.</li>
      </ul>
      {deliveryAddress && (
        <div className="grid gap-1">
          <p className="text-sm font-bold text-muted">Adresse de livraison</p>
          <p className="rounded-xl bg-surface px-3 py-2 whitespace-pre-line select-all">
            {deliveryAddress}
          </p>
        </div>
      )}
    </div>
  );
}
